import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { CurrenciesService } from '@core/services/currencies.service';
import { DataService, GoodRecord, invoiceCategory, InvoiceCategory, InvoiceDetailRecord, InvoiceRecord, InvoiceState, invoiceState, InvoiceType, UserRecord, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { TagsService } from '@core/services/tags.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { MessageBoxService } from '@routes/common/message-box.service';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigsService } from '@core/services/configs.service';

const lu = {
  goods: new Lookup<GoodRecord>(i => i.ID),
  users: new Lookup<UserRecord>(i => i.ID)
}

interface Group {
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  ImportUnitPrice: number;

  records: InvoiceDetailRecord[];
}

interface DetailExtractor<T> {

  count: (item: T) => number;
  actualQuantity: (item: T) => number;
  quoteQuantity: (item: T) => number;
  quoteUnitPrice: (item: T) => number;
  quoteIcon: string;
  subtotal: (item: T) => number;

  sum: (field: string, unit?: ProductUnit) => number;
}

@Component({
  templateUrl: './losses.component.html',
  styleUrls: ['./losses.component.scss']
})
export class LossesComponent implements OnInit {

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private mb: MessageBoxService,
    private api: ApiService,    
    private sync: SynchronizationService,
    private currencies: CurrenciesService,

    private _partners: PartnersService,
    private _tags: TagsService,
    private _users: UsersService,
    private modal: ModalService,
    public configs: ConfigsService
  ) { }

  public get loading(): boolean {
    return !this.sync.ready(UsersService, CurrenciesService, LossesComponent, TagsService, PartnersService);
  }

  public invoices = new Filterable<InvoiceRecord>({ extractor: i => [i.Tag, this._partners.get(i.PartnerID).Name], grouper: i => [{ name: 'Khách hàng', option: this._partners.get(i.PartnerID).Name, description: 'K' + i.PartnerID }] });
  public goods = new Filterable<GoodRecord>({
    extractor: i => [i.SKU + 'm' + i.Color + 'd' + i.ActualQuantity, i.SKU + 'd' + i.ActualQuantity],
    grouper: i => [
      { name: 'Nguồn nhập', option: i.ImportInvoiceTag, description: this.partner(i.ImportInvoiceTag) },
      { name: 'Loại hàng', option: i.Unit == 1 ? 'Hàng mét' : 'Hàng cân' }
    ]

  });

  public selected: { invoice: InvoiceRecord, details: Group[] } = { invoice: null, details: [] };

  private edited: boolean = false;

  private initialize() {

    this.selected.invoice = null;
    this.selected.details = [];
    this.invoices.source = [];
    this.goods.source = [];
    this.edited = false;

    let goods: GoodRecord[] = [];
    let invoices: InvoiceRecord[] = [];

    this.sync.with(LossesComponent).repeat(
      () => this.ds.goods('exportable').pipe(map(data => data ? commit<boolean>(() => goods = data).return(true) : false)),
      () => this.ds.invoices("exports").pipe(map(data => data ? commit<boolean>(() => invoices = data).return(true) : false))
    );

    this.sync.when(LossesComponent, CurrenciesService, UsersService, TagsService, PartnersService).commit(() => {

      lu.goods.source = goods;
      this.invoices.source = invoices.filter(i => i.PartnerID < 3);
    });
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Xử lý hàng mất");
    });

    this.initialize();
  }

  public get creatable(): boolean {
    return !this.selected.invoice && lu.goods.length > 0;
  }

  public get editable(): boolean {
    return this.selected.invoice && this.selected.invoice.ID == 0;
  }

  public get savable(): boolean {
    return this.selected.invoice && this.selected.invoice.ID == 0 && this.selected.invoice.Details.length > 0 && this.edited;
  }

  public get finishable(): boolean {
    return this.selected.invoice && this.selected.invoice.ID > 0 && this.selected.invoice.State == InvoiceState.Editable;
  }

  public user(id: number): UserRecord {
    return this._users.get(id);
  }

  public state = invoiceState;
  public category = invoiceCategory;

  public group: DetailExtractor<Group> = {
    count: i => i.records.length,
    actualQuantity: i => i.records.sum(i => i.ActualQuantity),
    quoteQuantity: i => i.records.sum(i => i.QuoteQuantity),
    quoteUnitPrice: i => i.records.length > 0 ? i.records.sum(i => i.QuoteUnitPrice) / i.records.length : 0,
    quoteIcon: 'đ',
    subtotal: i => i.records.sum(i => i.QuoteUnitPrice * i.QuoteQuantity),

    sum: (field: string, unit?: ProductUnit) => {
      switch (field) {
        case 'Count': return this.selected.details.sum(i => !unit || unit == i.Unit ? i.records.length : 0);
        case 'ActualQuantity': return this.selected.details.sum(i => !unit || unit == i.Unit ? this.group.actualQuantity(i) : 0);
        case 'Reduced': return this.selected.details.sum(i => !unit || unit == i.Unit ? this.group.actualQuantity(i) - this.group.quoteQuantity(i) : 0);
        case 'QuoteQuantity': return this.selected.details.sum(i => !unit || unit == i.Unit ? this.group.quoteQuantity(i) : 0);
        case 'Subtotal': return this.selected.details.sum(i => !unit || unit == i.Unit ? this.group.subtotal(i) : 0);
        default: return null;
      }
    }
  };

  public extractor(field: string): SortableExtractor<InvoiceRecord> {
    return {
      extract: i => {
        switch (field) {
          case 'Products': return i.SKUCount;
          case 'Quantity': return i.LengthCount;
          case 'QuoteQuantity': return i.LengthQuoteQuantity;
          case 'Creator': return this.user(i.CreatorID).Username;
          default: return i[field];
        }
      }
    }
  }

  public create() {
    this.open({
      ID: 0,
      Tag: null,
      Type: InvoiceType.Export,
      State: InvoiceState.Editable,
      Category: InvoiceCategory.Lost,
      Allocations: [],
      Details: [],
      CreatorID: this.api.user.ID,
      UpdatorID: this.api.user.ID,
      FirstStamp: new Date().getTime(),
      LastStamp: new Date().getTime(),
      PartnerID: 2,
      Icon: 'đ',
      Symbol: 'VND',
      Stamp: new Date().getTime(),
      Total: 0
    });    
  }

  public partner(tag: string): string {
    let t = this._tags.get(tag);
    if (!t) return '';

    let p = this._partners.get(t.PartnerID);
    return p ? p.Name : '';
  }

  public add(good: GoodRecord) {

    let detail = this.selected.details.first(i => i.SKU == good.SKU && i.Color == good.Color && i.ImportUnitPrice == good.ImportUnitPrice, {
      SKU: good.SKU,
      Color: good.Color,
      Unit: good.Unit,
      ImportUnitPrice: good.VendorID == 1 ? good.ImportUnitPrice : 0,
      records: []
    });

    let record: InvoiceDetailRecord = {
      ID: good.ID,
      SKU: good.SKU,
      Color: good.Color,
      Unit: good.Unit,
      ActualQuantity: good.ActualQuantity,
      QuoteQuantity: good.ImportQuoteQuantity,
      QuoteUnitPrice: good.ImportSymbol == 'CNY' ? Math.round(good.ImportUnitPrice * this.currencies.get('CNY').Conversion / this.currencies.get("VND").RoundingFactor) * this.currencies.get("VND").RoundingFactor : good.ImportUnitPrice,      
      ImportOrderID: good.ImportOrderID,
      ExportOrderID: 0,      
      IsPriceEditable: false
    };

    detail.records.push(record);
    this.selected.invoice.Details.push(record);

    this.goods.source = this.goods.source.filter(i => i != good);

    if (detail.records.length == 1) this.selected.details.push(detail);

    this.edited = true;
  }

  public remove(record: InvoiceDetailRecord) {

    let detail = this.selected.details.first(i => i.records.contains(record));

    detail.records.remove(record);
    this.selected.invoice.Details.remove(record);

    this.goods.source = this.goods.source.union([lu.goods.get(record.ID)]);

    if (detail.records.length < 1) this.selected.details.remove(detail);

    this.edited = true;
  }

  public open(invoice: InvoiceRecord) {

    if (invoice.ID > 0 && invoice.Details.length == 0) {
      this.sync.with(LossesComponent).repeat(() => this.ds.invoices('get', { ID: invoice.ID }).pipe(map(invoices => {
        if (invoices && invoices.length == 1) {
          invoice.Details = invoices[0].Details;
          return true;
        }
        return false;
      })));
    }

    if (invoice.State == InvoiceState.Confirmed) {
      this.sync.when(LossesComponent, PartnersService).commit(() => {
        let modal = this.modal.show(ViewerComponent, { state: { invoice: invoice } });
        modal.content.unconfirmed.subscribe(result => invoice.State = result.State);
      });

      return;
    }

    this.sync.when(LossesComponent).commit(() => {

      invoice = { ...invoice, Details: invoice.Details.map(i => ({ ...i })) };

      let test = invoice.Details.lookup(i => i.ID, i => true);
      this.goods.source = lu.goods.source.filter(i => !test[i.ID]);

      this.selected.details = invoice.Details.group(i => [i.SKU, i.Color, i.Unit, i.ImportInvoiceTag].join('/')).map<Group>(i => ({
        SKU: i.items[0].SKU,
        Color: i.items[0].Color,
        Unit: i.items[0].Unit,
        ImportUnitPrice: i.items[0].QuoteUnitPrice,
        Reduced: i.items.sum(x => x.ActualQuantity - x.QuoteQuantity),
        records: i.items
      }));

      this.selected.invoice = invoice;
      this.edited = false;
    });
  }

  public close() {

    if (this.savable) {

      this.mb.confirms('discourage', "Đơn đặt hàng có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?", () => {
        this.initialize();
        return new Observable<boolean>(i => i.next(true));
      });
    }
    else {
      this.initialize();
    }
  }

  public save() {

    this.sync.with(LossesComponent).commit(done => {

      this.ds.invoices(this.selected.invoice.ID > 0 ? 'update' : 'create', this.selected.invoice).subscribe(invoices => {

        if (invoices && invoices.length == 1) {
          this.open(invoices[0]);
        }

        done();
      });
    });    
  }

  public confirm() {

    this.mb.confirms('discourage', this.selected.invoice.PartnerID == 1 ? "Bạn muốn xác nhận xưởng chịu trách nhiệm cho đơn hủy hàng " + (this.selected.invoice.Category == InvoiceCategory.Lost ? "mất" : "lỗi") + " đang mở?" : "Bạn muốn xác nhận đơn hủy hàng mất đang mở?",

      () => {

        this.sync.notify(LossesComponent, false);

        return this.ds.invoices('confirm', this.selected.invoice).pipe(map(invoices => {

          this.sync.notify(LossesComponent, true);

          if (invoices) {
            this.invoices.source = this.invoices.source.filter(i => i.ID != this.selected.invoice.ID).union(invoices);
            this.edited = false;
            this.close();

            return true;
          }

          return false;
        }));
      });
  }

  public cancel() {

    if (this.selected.invoice.ID == 0 && this.selected.invoice.Details.length == 0) {
      this.close();
      return;
    }

    this.mb.confirms('encourage', this.selected.invoice.PartnerID == 1 ? "Bạn muốn xác nhận công ty chịu trách nhiệm cho đơn hủy hàng " + (this.selected.invoice.Category == InvoiceCategory.Lost ? "mất" : "lỗi") + " đang mở?" : "Bạn muốn hủy đơn hủy hàng mất đang mở?", () => {

        this.sync.notify(LossesComponent, false);

        return this.ds.invoices('cancel', this.selected.invoice).pipe(map(invoices => {

          this.sync.notify(LossesComponent, true);

          if (invoices && invoices.length == 1) {

            this.invoices.source = this.invoices.source.filter(i => i.ID != this.selected.invoice.ID).union(invoices[0].ID == this.selected.invoice.ID ? [] : invoices);
            this.edited = false;

            if (this.selected.invoice.ID == invoices[0].ID) {
              this.close();
            }
            else {
              this.open(invoices[0]);
            }

            return true;
          }

          return false;
        }));
      });
  }
}
